import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ContactModal from "../ContactModal";
import { Modal, Button, Input } from "../ui";
import { formatPhoneNumber } from "../../helpers/formatPhoneNumber";

// Icons
const SpinnerIcon = ({ className }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const NewTicketModal = ({ modalOpen, onClose }) => {
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [newContact, setNewContact] = useState({});
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedContact(null);
  };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId: contactId,
        userId: user.id,
        status: "open",
      });
      history.push(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
    handleClose();
  };

  const handleSelectContact = (contact) => {
    if (contact.number) {
      setSelectedContact(contact);
      setSearchParam(`${contact.name} - ${formatPhoneNumber(contact.number)}`);
    } else {
      setNewContact({ name: contact.name });
      setContactModalOpen(true);
    }
    setIsDropdownOpen(false);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = (contact) => {
    handleSaveTicket(contact.id);
  };

  const handleKeyPress = (e) => {
    if (loading || !selectedContact) return;
    if (e.key === "Enter") {
      handleSaveTicket(selectedContact.id);
    }
  };

  const handleAddNewContact = () => {
    if (searchParam.trim()) {
      setNewContact({ name: searchParam.trim() });
      setContactModalOpen(true);
      setIsDropdownOpen(false);
    }
  };

  return (
    <>
      <ContactModal
        open={contactModalOpen}
        initialValues={newContact}
        onClose={handleCloseContactModal}
        onSave={handleAddNewContactTicket}
      />

      <Modal
        open={modalOpen}
        onClose={handleClose}
        title={i18n.t("newTicketModal.title")}
        maxWidth="sm"
      >
        <div className="space-y-4">
          {/* Contact Autocomplete */}
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1">
              {i18n.t("newTicketModal.fieldLabel")}
            </label>
            <div className="relative">
              <Input
                value={searchParam}
                onChange={(e) => {
                  setSearchParam(e.target.value);
                  setSelectedContact(null);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                onKeyPress={handleKeyPress}
                placeholder={i18n.t("newTicketModal.fieldLabel")}
                autoFocus
              />
              {loading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <SpinnerIcon className="w-5 h-5 text-dark-400" />
                </div>
              )}
            </div>

            {/* Dropdown */}
            {isDropdownOpen && (options.length > 0 || (searchParam.length >= 3 && !loading)) && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-dark-800 border border-dark-200 dark:border-dark-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {options.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => handleSelectContact(contact)}
                    className="w-full px-4 py-2 text-left text-sm text-dark-700 dark:text-dark-200 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors"
                  >
                    {contact.number
                      ? `${contact.name} - ${formatPhoneNumber(contact.number)}`
                      : `${i18n.t("newTicketModal.add")} ${contact.name}`}
                  </button>
                ))}

                {searchParam.length >= 3 && !loading && (
                  <button
                    type="button"
                    onClick={handleAddNewContact}
                    className="w-full px-4 py-2 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors flex items-center gap-2 border-t border-dark-200 dark:border-dark-700"
                  >
                    <PlusIcon className="w-4 h-4" />
                    {i18n.t("newTicketModal.add")} {searchParam}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-dark-200 dark:border-dark-700">
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("newTicketModal.buttons.cancel")}
          </Button>
          <Button
            type="button"
            disabled={!selectedContact || loading}
            onClick={() => handleSaveTicket(selectedContact?.id)}
          >
            {loading && <SpinnerIcon className="w-4 h-4 mr-2" />}
            {i18n.t("newTicketModal.buttons.ok")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default NewTicketModal;
